import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ComponentType, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CaseDocument, CaseDocumentWithPresignedUrl, User } from "pwapi";

import { openDocument } from "shared/actions/caseDetailActions";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import LinkButton from "components/LinkButton";
import PatientSexAge from "components/PatientSexAge";
import ApplicationState from "store/ApplicationState";

import { getAdditionalDocuments } from "./ReportList";
import { getFullNameStr, shouldDisplayPatient } from "../caseList/CaseColumns";

const ReportColumns =
  <P extends object>(Component: ComponentType<P>) =>
  (props: any) => {
    const dispatch = useDispatch();

    const features: FeatureFlags = useSelector(
      ({ sys }: ApplicationState) => sys.features
    );
    const user: User | undefined = useSelector(
      ({ auth }: ApplicationState) => auth.user
    );

    const columns = useMemo(
      () =>
        [
          {
            Header: "Accession",
            accessor: "accession",
            Cell: ({ row }: any) => (
              <span className="column__longName" title="Accession">
                {row.original.accession}
              </span>
            ),
          },
          {
            Header: "Collected Date",
            accessor: "collected_date",
            Cell: ({ row }: any) => (
              <span>
                {row.original.collected_date &&
                  DateTime.fromISO(row.original.collected_date).toLocaleString(
                    DateTime.DATETIME_MED_WITH_WEEKDAY
                  )}
              </span>
            ),
          },
          {
            Header: "Received Date",
            accessor: "received_date",
            Cell: ({ row }: any) => (
              <span>
                {row.original.received_date &&
                  DateTime.fromISO(row.original.received_date).toLocaleString(
                    DateTime.DATETIME_MED_WITH_WEEKDAY
                  )}
              </span>
            ),
          },
          ...(props.reportType === "report"
            ? [
                {
                  Header: "Report Signed Date",
                  accessor: "diagnoses__report_signed",
                  Cell: ({ row }: any) => (
                    <span>
                      {row.original.report_signed &&
                        DateTime.fromISO(
                          row.original.report_signed
                        ).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
                    </span>
                  ),
                },
              ]
            : []),
          {
            Header: "Active Assignment",
            accessor: "active_assignment",
            Cell: ({ row }: any) => (
              <>
                <span>
                  {getFullNameStr(
                    row.original.active_assignment?.last_name,
                    row.original.active_assignment?.first_name
                  )}
                </span>
              </>
            ),
          },
          {
            Header: "Clinician",
            accessor: "ordering_doctor",
            Cell: ({ row }: any) => (
              <>
                <span>
                  {getFullNameStr(
                    row.original.ordering_doctor?.last_name,
                    row.original.ordering_doctor?.first_name
                  )}
                </span>
                <small title="Clinic">
                  {row.original.patient?.clinic?.name || ""}
                </small>
              </>
            ),
          },
          {
            Header: "Patient",
            accessor: "patient",
            Cell: ({ row }: any) => {
              return shouldDisplayPatient(row.original.patient) ? (
                <>
                  <span className="column__longName" title="Patient Name">
                    {getFullNameStr(
                      row.original.patient.last_name,
                      row.original.patient.first_name,
                      row.original.patient.middle_initial
                    )}
                  </span>
                  <small>
                    <PatientSexAge patient={row.original.patient} />
                  </small>
                </>
              ) : (
                <></>
              );
            },
          },
          {
            Header: "Actions",
            disableSortBy: true,
            minWidth: "10em",
            sticky: "right",
            Cell: ({ row }: any) => {
              const category =
                props.reportType === "report" ? "Report" : "Requisition Form";

              const document: CaseDocument | undefined =
                row.original.case_document_views?.find(
                  (doc: CaseDocumentWithPresignedUrl) =>
                    props.reportType === "report"
                      ? doc.category ===
                        CaseDocumentWithPresignedUrl.category.CASE_REPORT
                      : doc.category ===
                          CaseDocumentWithPresignedUrl.category
                            .REQUISITION_FORM &&
                        doc.status !==
                          CaseDocumentWithPresignedUrl.status.PENDING
                );

              const documentName = `${
                row.original.patient.last_name
                  ? `${row.original.patient.last_name}_`
                  : ""
              }${row.original.patient.first_name || "UNKNOWN"}${
                row.original.patient.middle_initial
                  ? `_${row.original.patient.middle_initial}`
                  : ""
              }-${
                row.original.patient.date_of_birth
                  ? DateTime.fromISO(
                      row.original.patient.date_of_birth
                    ).toFormat(
                      "yyyyMMdd"
                    ) /* This timestamp does not get localized because it refers to a filename */
                  : ""
              }-${row.original.accession}-${document?.category.replace(
                "-",
                "_"
              )}`;

              const additionalDocuments = getAdditionalDocuments(
                row.original.case_document_views,
                props.reportType
              );

              const allAdditionalDocumentsViewed = additionalDocuments.every(
                (doc) => doc?.has_been_viewed
              );

              return (
                <div className="columnActions__container">
                  {document?.id && (
                    <PathologyWatchButton
                      title="download"
                      onClick={() => {
                        row.original?.id &&
                          document.id &&
                          dispatch(
                            openDocument(
                              row.original.id,
                              document.id,
                              "document-list",
                              user,
                              documentName
                            )
                          );
                      }}
                    >
                      <>
                        {category} &nbsp;
                        <FontAwesomeIcon
                          icon="file-download"
                          color="var(--blue)"
                          style={{ fontSize: "1.2em" }}
                        />
                      </>
                    </PathologyWatchButton>
                  )}
                  {additionalDocuments && additionalDocuments.length ? (
                    <div {...row.getToggleRowExpandedProps()}>
                      <LinkButton
                        className={classNames(
                          "columnActions__container__more",

                          {
                            "columnActions__container__more--downloaded":
                              allAdditionalDocumentsViewed,
                          }
                        )}
                      >
                        <>
                          {additionalDocuments.length}&nbsp;More&nbsp;
                          <span>
                            {row.isExpanded ? (
                              <FontAwesomeIcon icon="angle-down" />
                            ) : (
                              <FontAwesomeIcon icon="angle-right" />
                            )}
                          </span>
                        </>
                      </LinkButton>
                    </div>
                  ) : undefined}
                </div>
              );
            },
          },
        ] as any,
      [user, props.reportType]
    );

    return <Component columns={columns} features={features} {...props} />;
  };

export default ReportColumns;
